/* eslint-disable jira/import-whitelist */
import BOARD_QUERY from '@jira/spa-apps__next-gen-board/src/__generated__/BoardWithRouterRelayQuery$parameters.ts';
import type { BoardWithRouterRelayQuery } from '@jira/spa-apps__next-gen-board/src/__generated__/BoardWithRouterRelayQuery.graphql.ts';
import BOARD_TMP_MATURE_EXAMPLE_GUPS_QUERY, {
	type BoardTmpMatureExampleGupsAnalyticsQuery,
} from '@jira/spa-apps__next-gen-board/src/__generated__/BoardTmpMatureExampleGupsAnalyticsQuery.graphql.ts';
import type { BoardScopeGQL } from '@jira/software__board-fetch-scope-critical/src/BoardScopeGQL.types.tsx';
import QUERY from '@jira/software__board/src/header/controls-bar/filter-bar/board-custom-filters/__generated__/BoardCustomFilterRelayQuery$parameters';
import type { BoardCustomFilterRelayQuery } from '@jira/software__board/src/header/controls-bar/filter-bar/board-custom-filters/__generated__/BoardCustomFilterRelayQuery.graphql';
import type { BoardFilterRefinementQuery as boardFilterRefinementQueryType } from '@jira/software__board/src/header/controls-bar/board-filter-refinement/__generated__/BoardFilterRefinementQuery.graphql';
import BOARD_FILTER_REFINEMENT_QUERY from '@jira/software__board/src/header/controls-bar/board-filter-refinement/__generated__/BoardFilterRefinementQuery$parameters';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import type { PreloadedQuery } from 'react-relay';
import { createAri } from '@jira/platform__utils-ari/src/index.tsx';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';

import {
	RESOURCE_TYPE_NEXTGEN_BOARD,
	RESOURCE_TYPE_NEXTGEN_BOARD_CUSTOM_FILTERS,
	RESOURCE_TYPE_NEXTGEN_BOARD_RELAY,
	RESOURCE_TYPE_NEXTGEN_BOARD_FILTER_REFINEMENT,
	RESOURCE_TYPE_NEXTGEN_BOARD_TMP_MATURE_EXAMPLE_GUPS,
} from './resourceTypeNextgenBoard.constants.tsx';

export const boardResource: RouteResource<BoardScopeGQL | null> =
	createResource<BoardScopeGQL | null>({
		type: RESOURCE_TYPE_NEXTGEN_BOARD,
		getKey: ({ match }) => match.params.boardId || '',
		getDataLoader: () =>
			import(
				/* webpackChunkName: "async-nextgen-common", jiraSpaEntry: "async-nextgen-common" */ './getData.tsx'
			),
		// SSR doesn't care about this and we can't check FFs here because its not in the render path
		maxAge: 1000 * 60 * 60, // 1 hour max age
		isBrowserOnly: true,
	});

export const customFiltersResource: RouteResource<
	PreloadedQuery<BoardCustomFilterRelayQuery, Record<string, unknown>>
> = createRelayResource<BoardCustomFilterRelayQuery>({
	type: RESOURCE_TYPE_NEXTGEN_BOARD_CUSTOM_FILTERS,
	getQuery: ({ match }, { tenantContext }) => ({
		parameters: QUERY,
		variables: {
			boardId: createAri({
				resourceOwner: 'jira-software',
				cloudId: tenantContext.cloudId,
				resourceType: 'board',
				resourceId: String(match.params.boardId),
			}),
		},
		options: { fetchPolicy: 'store-and-network' },
	}),
	isBrowserOnly: true,
});

export const boardFilterRefinementResource: RouteResource<
	PreloadedQuery<boardFilterRefinementQueryType, Record<string, unknown>>
> = createRelayResource<boardFilterRefinementQueryType>({
	type: RESOURCE_TYPE_NEXTGEN_BOARD_FILTER_REFINEMENT,
	getQuery: ({ match }, { tenantContext }) => ({
		parameters: BOARD_FILTER_REFINEMENT_QUERY,
		variables: {
			cloudId: tenantContext.cloudId,
			boardId: createAri({
				resourceOwner: 'jira-software',
				cloudId: tenantContext.cloudId,
				resourceType: 'board',
				resourceId: String(match.params.boardId),
			}),
			filterRefinementScope: {
				board: {
					boardId: Number(match.params.boardId),
				},
			},
		},
		options: { fetchPolicy: 'store-and-network' },
	}),
	isBrowserOnly: true,
});

export const boardTmpMatureExampleGupsResource: RouteResource<
	PreloadedQuery<BoardTmpMatureExampleGupsAnalyticsQuery, Record<string, unknown>>
> = createRelayResource<BoardTmpMatureExampleGupsAnalyticsQuery>({
	type: RESOURCE_TYPE_NEXTGEN_BOARD_TMP_MATURE_EXAMPLE_GUPS,
	getQuery: (_ctx, { tenantContext }) => ({
		parameters: BOARD_TMP_MATURE_EXAMPLE_GUPS_QUERY,
		variables: {
			cloudId: tenantContext.cloudId,
			includeGups: true,
		},
		options: { fetchPolicy: 'store-and-network' },
	}),
	isBrowserOnly: true,
});

// oxlint-disable-next-line typescript/no-explicit-any
export const conditionalBoardTmpMatureExampleGupsResource: RouteResource<any> =
	resourceWithCondition(
		() => !fg('critical_board_query_with_relay') && fg('spice-gups-tmp-board-gate'),
		boardTmpMatureExampleGupsResource,
	);

export const boardRelayResource: RouteResource<
	PreloadedQuery<BoardWithRouterRelayQuery, Record<string, unknown>>
> = createRelayResource<BoardWithRouterRelayQuery>({
	type: RESOURCE_TYPE_NEXTGEN_BOARD_RELAY,
	getQuery: ({ match }, { tenantContext }) => ({
		parameters: BOARD_QUERY,
		variables: {
			boardId: createAri({
				resourceOwner: 'jira-software',
				cloudId: tenantContext.cloudId,
				resourceType: 'board',
				resourceId: String(match.params.boardId),
			}),
		},
		options: { fetchPolicy: 'store-and-network' },
	}),
	isBrowserOnly: true,
});
